import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '../services/auth'
import { useShopStore } from './shop-store'

export interface User {
  id: string
  email: string
  name?: string
  role?: string
  shop?: string
  stores?: number // Count of user stores from /users/me response
  timezone?: string
  currency?: string
  default_date_range?: string
  theme?: string
}

/** Value stored in selected_store when user chooses "All Stores" in the dashboard store dropdown. */
export const ALL_STORES_VALUE = '__all__'

export interface UserStore {
  id: string
  name?: string
  domain: string
  currency?: string
  created_at?: string
}

// ShopDetails interface moved to shop-store.ts
export type { ShopDetails } from './shop-store'

type TokenType = 'USER' | 'SHOP' | 'NONE'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const tokenType = ref<TokenType>('NONE')
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const needsStoreLink = ref(false)
  const shopLinkedToAnotherUser = ref(false)
  const userStores = ref<UserStore[]>([])
  const isFetchingUserStores = ref(false)
  const storesLoaded = ref(false)
  let storesPromise: Promise<UserStore[]> | null = null

  /** Mirrors `localStorage.selected_store` for reactive calendar timezone (All Stores vs single). */
  const selectedStoreLocalId = ref<string | null>(null)

  const syncSelectedStoreLocalIdFromStorage = () => {
    if (typeof localStorage === 'undefined') return
    selectedStoreLocalId.value = localStorage.getItem('selected_store')
  }

  // Get shop store instance (for computed properties)
  const shopStore = useShopStore()

  // Computed
  const isAuthenticated = computed(() => {
    return !!token.value && authService.isAuthenticated()
  })

  const isUserToken = computed(() => {
    return tokenType.value === 'USER'
  })

  const isShopToken = computed(() => {
    return tokenType.value === 'SHOP'
  })

  const bearerToken = computed(() => {
    return authService.getBearerToken()
  })

  // Get shop details from shop store (computed for backward compatibility)
  // Components should use useShopStore() directly for better performance
  const shopDetails = computed(() => {
    return shopStore.shopDetails
  })

  // Get isInstallCompleted from shop store (computed for backward compatibility)
  // Components should use useShopStore() directly for better performance
  const isInstallCompleted = computed(() => {
    return shopStore.isInstallCompleted
  })

  // Actions
  const initializeAuth = async () => {
    isLoading.value = true
    try {
      const storedToken = authService.getToken()
      const storedUser = authService.getUserInfo()

      if (storedToken && authService.isAuthenticated()) {
        token.value = storedToken
        user.value = storedUser || null

        // Determine token type from localStorage
        if (authService.isUserToken()) {
          tokenType.value = 'USER'
          // For user tokens, fetch user details and stores
          try {
            await fetchCurrentUser()
            await fetchUserStores()
          } catch (error) {
            // If fetch fails, user might not be valid, but continue with shop details
          }
        } else {
          tokenType.value = 'SHOP'
        }

        // Shop details will be fetched in bootstrap/initializeSession
        // Don't fetch here to avoid cross-store orchestration
      } else {
        // Clear invalid/expired tokens
        tokenType.value = 'NONE'
        logout()
      }
    } catch (error) {
      tokenType.value = 'NONE'
      logout()
    } finally {
      isLoading.value = false
      syncSelectedStoreLocalIdFromStorage()
    }
  }

  // fetchShopDetails moved to shop-store.ts
  // Use shopStore.fetchShopDetails() instead

  const handleShopifyCallback = async (jwt: string): Promise<boolean> => {
    isLoading.value = true
    error.value = null

    try {
      const success = authService.handleShopifyCallback(jwt)

      if (success) {
        // Get shop token explicitly (not user token)
        const shopToken = authService.getShopToken()
        if (!shopToken) {
          error.value = 'Shop token not found after callback'
          return false
        }

        token.value = shopToken
        user.value = authService.getUserInfo() || null
        error.value = null

        // Only set tokenType to SHOP if we don't already have a USER token
        // This preserves user session if user is already logged in
        if (tokenType.value !== 'USER') {
          tokenType.value = 'SHOP'
        }

        // Fetch shop details using explicit shop token (not current token which might be user token)
        try {
          const { apiService } = await import('../services/api')
          const shopData = await apiService.getShopDetailsWithToken(shopToken)
          shopStore.shopDetails = shopData
        } catch (err) {
          // If explicit fetch fails, fall back to regular fetch
          // But this should not happen in normal flow
          console.warn('Failed to fetch shop details with explicit token, falling back', err)
        }

        return true
      } else {
        error.value = 'Invalid or expired JWT token'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Authentication failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  const login = async (jwt: string): Promise<boolean> => {
    return await handleShopifyCallback(jwt)
  }

  /**
   * Handle shop JWT from Shopify callback
   * Stores shop JWT without overwriting user token
   * Returns shop details for routing decisions
   */
  const handleShopJwt = async (jwt: string): Promise<any> => {
    try {
      // If user is currently the demo account, the USER token must not override shop-token access.
      // Drop the demo user session before processing the shop JWT so subsequent API calls use the shop token.
      if (tokenType.value === 'USER' && authService.isDemoAccount()) {
        authService.clearUserSession()
        clearStoresCache()
        tokenType.value = 'NONE'
        token.value = null
        user.value = null
      }

      // Validate JWT using authService (validates and stores if valid)
      const isValid = authService.handleShopifyCallback(jwt)
      if (!isValid) {
        throw new Error('Invalid or expired JWT token')
      }

      // Ensure we can access as shop when coming from Shopify callback.
      // If we previously cleared demo session above, tokenType is NONE here.
      if (tokenType.value === 'NONE') {
        tokenType.value = 'SHOP'
      }
      if (tokenType.value === 'SHOP') {
        token.value = jwt
        const userInfo = authService.getUserInfo()
        user.value = (userInfo as User) || null
      }

      // Fetch shop details using explicit shop token (not default auth header)
      // This ensures we use the shop JWT, not the current user token
      const { apiService } = await import('../services/api')
      const shopData = await apiService.getShopDetailsWithToken(jwt)

      // Store shop details in shopStore
      shopStore.shopDetails = shopData

      return shopData
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to process shop JWT')
    }
  }

  const updateJWT = async (newJwt: string): Promise<boolean> => {
    isLoading.value = true
    error.value = null

    try {
      // Validate the new JWT
      const success = authService.handleShopifyCallback(newJwt)

      if (success) {
        // Get shop token explicitly
        const shopToken = authService.getShopToken()
        if (!shopToken) {
          error.value = 'Shop token not found after update'
          return false
        }

        // Update token and user info
        token.value = shopToken
        user.value = authService.getUserInfo() || null
        error.value = null

        // Only set tokenType to SHOP if we don't already have a USER token
        if (tokenType.value !== 'USER') {
          tokenType.value = 'SHOP'
        }

        // Fetch shop details using explicit shop token
        try {
          const { apiService } = await import('../services/api')
          const shopData = await apiService.getShopDetailsWithToken(shopToken)
          shopStore.shopDetails = shopData
        } catch (err) {
          console.warn('Failed to fetch shop details with explicit token, falling back', err)
        }

        return true
      } else {
        error.value = 'Invalid or expired JWT token'
        return false
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update JWT'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // completeInstall moved to shop-store.ts
  // Use shopStore.completeInstall() instead

  const logout = async () => {
    authService.logout()
    token.value = null
    user.value = null
    tokenType.value = 'NONE'
    error.value = null
    isLoading.value = false
    needsStoreLink.value = false
    shopLinkedToAnotherUser.value = false

    // Clear stores cache
    clearStoresCache()

    // Clear selected store
    localStorage.removeItem('selected_store')

    // Clear shop details
    try {
      const { useShopStore } = await import('./shop-store')
      const shopStore = useShopStore()
      shopStore.clearShopDetails()
    } catch (error) {
      // Error clearing shop details, continue with logout
    }

    // Clear cached subscription data on logout
    try {
      const { useSubscription } = await import('../composables/useSubscription')
      const { resetSubscription } = useSubscription()
      resetSubscription()
    } catch (error) {
      // Error resetting subscription cache, continue with logout
    }
  }

  const redirectToShopifyAuth = () => {
    authService.redirectToShopifyAuth()
  }

  const clearError = () => {
    error.value = null
  }

  const verifyShopAccess = async (): Promise<boolean> => {
    if (!isAuthenticated.value) {
      return false
    }

    try {
      // Fetch shop details to verify access
      const { useShopStore } = await import('./shop-store')
      const shopStore = useShopStore()
      await shopStore.fetchShopDetails({})

      // If successful and shop details are loaded, access is valid
      return shopStore.shopDetails !== null
    } catch (error: any) {
      // Check for 401/403 status codes
      if (error.message && (error.message.includes('401') || error.message.includes('403'))) {
        // Invalid shop access, logout user
        logout()
        return false
      }

      // For other errors (network, etc.), assume access is still valid
      // to avoid logging out users due to temporary issues
      return true
    }
  }

  const userLogin = async (data: { email: string; password: string }): Promise<boolean> => {
    error.value = null

    try {
      const { apiService } = await import('../services/api')
      const response = await apiService.login(data)

      if (response.token && response.user) {
        // Store user token in localStorage first
        authService.handleUserAuth({ token: response.token, user: response.user })

        // Set token and user in store state directly from response
        // This ensures the store state is immediately updated
        token.value = response.token
        user.value = response.user
        tokenType.value = 'USER'
        error.value = null

        // Fetch current user to get stores count from /users/me
        // This ensures we have the stores count without calling fetchUserStores
        try {
          await fetchCurrentUser()
        } catch (err) {
          // If fetchCurrentUser fails, continue with login but stores count won't be available
          console.warn('Failed to fetch current user after login:', err)
        }

        // Only fetch full store list if user has stores (to set selected store)
        // This avoids unnecessary API call if user has no stores
        if (user.value?.stores && user.value.stores > 0) {
          await fetchUserStores()
        }

        return true
      } else {
        error.value = 'Invalid response from server'
        return false
      }
    } catch (err: any) {
      error.value = err?.message || 'Login failed'
      return false
    }
  }

  const userSignup = async (data: { name?: string; email: string; password: string }): Promise<boolean> => {
    error.value = null

    try {
      const { apiService } = await import('../services/api')
      const response = await apiService.signup(data)

      if (response.token && response.user) {
        authService.handleUserAuth({ token: response.token, user: response.user })
        token.value = response.token
        user.value = response.user
        tokenType.value = 'USER'
        error.value = null

        // Fetch user stores
        await fetchUserStores()

        return true
      } else {
        error.value = 'Invalid response from server'
        return false
      }
    } catch (err: any) {
      error.value = err?.message || 'Signup failed'
      return false
    }
  }

  const fetchCurrentUser = async (): Promise<void> => {
    if (!token.value) {
      return
    }

    try {
      const { apiService } = await import('../services/api')
      const userData = await apiService.getCurrentUser()
      user.value = userData
      authService.setUserInfo(userData, true)
    } catch (err: any) {
      // Force logout if we can't fetch user details
      await logout()
      throw err
    }
  }

  const fetchUserStores = async (force = false): Promise<UserStore[]> => {
    // Return cached stores if already loaded and not forcing refresh
    if (!force && storesLoaded.value) {
      return userStores.value
    }

    // Return existing promise if already fetching
    if (storesPromise) {
      return storesPromise
    }

    isFetchingUserStores.value = true
    storesPromise = (async () => {
      try {
        const { apiService } = await import('../services/api')
        const response = await apiService.getUserStores()
        const stores = Array.isArray(response) ? response : response?.stores || []
        userStores.value = stores
        storesLoaded.value = true

        // Check if current selected store from localStorage exists in the new stores list
        const currentSelectedStore = localStorage.getItem('selected_store')
        const selectedStoreExists = currentSelectedStore
          ? stores.some((store: UserStore) => String(store.id) === String(currentSelectedStore))
          : false

        if (stores.length === 0) {
          // No stores - clear selection
          await updateSelectedStore(stores)
        } else if (!selectedStoreExists) {
          // Selected store doesn't exist in new list - update to first store or preserve if valid
          await updateSelectedStore(stores)
        } else {
          // Selected store exists and is valid - ensure shop_id is set and fetch shop details
          // Don't call updateSelectedStore to avoid resetting, but ensure shop_id is set
          if (currentSelectedStore) {
            localStorage.setItem('shop_id', currentSelectedStore)

            // Fetch shop details for the selected store if we have a user token
            if (authService.isUserToken()) {
              try {
                await fetchShopDetailsForStore(currentSelectedStore)
              } catch (err) {
                // Failed to fetch shop details for selected store
              }
            }
          }
        }

        return userStores.value
      } catch (error: any) {
        console.error('[fetchUserStores] Error:', {
          error: error?.message,
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          timestamp: new Date().toISOString(),
        })
        userStores.value = []
        storesLoaded.value = false
        return []
      } finally {
        isFetchingUserStores.value = false
        storesPromise = null
        syncSelectedStoreLocalIdFromStorage()
      }
    })()

    return storesPromise
  }

  const clearStoresCache = () => {
    userStores.value = []
    storesLoaded.value = false
    storesPromise = null
  }

  const updateSelectedStore = async (stores: UserStore[], explicitStoreId?: string | null): Promise<void> => {
    if (stores.length === 0) {
      localStorage.removeItem('selected_store')
      localStorage.removeItem('shop_id')
      const { useShopStore } = await import('./shop-store')
      const shopStore = useShopStore()
      shopStore.clearShopDetails()
      syncSelectedStoreLocalIdFromStorage()
      return
    }

    // "All Stores" selection: keep selected_store as __all__ for dashboard, use first store for shop context
    if (explicitStoreId === ALL_STORES_VALUE) {
      localStorage.setItem('selected_store', ALL_STORES_VALUE)
      const firstStoreId = String(stores[0].id)
      localStorage.setItem('shop_id', firstStoreId)
      if (authService.isUserToken()) {
        try {
          await fetchShopDetailsForStore(firstStoreId)
        } catch {
          // Failed to fetch shop details for fallback store
        }
      }
      syncSelectedStoreLocalIdFromStorage()
      return
    }

    let currentShopId: string | null = null

    // If explicit store ID is provided, validate it exists in stores list
    if (explicitStoreId) {
      const storeExists = stores.some((store) => String(store.id) === String(explicitStoreId))
      if (storeExists) {
        currentShopId = String(explicitStoreId)
      }
    }

    // If no explicit ID or explicit ID not found, check localStorage
    if (!currentShopId) {
      const selectedStoreId = localStorage.getItem('selected_store')
      if (selectedStoreId === ALL_STORES_VALUE) {
        // Keep "All Stores" selection; use first store for shop context
        localStorage.setItem('selected_store', ALL_STORES_VALUE)
        currentShopId = String(stores[0].id)
        localStorage.setItem('shop_id', currentShopId)
        if (authService.isUserToken()) {
          try {
            await fetchShopDetailsForStore(currentShopId)
          } catch {
            // Failed to fetch shop details for fallback store
          }
        }
        syncSelectedStoreLocalIdFromStorage()
        return
      }
      if (selectedStoreId) {
        const storeExists = stores.some((store) => String(store.id) === String(selectedStoreId))
        if (storeExists) {
          currentShopId = selectedStoreId
        }
      }
    }

    // If still no valid selected store, use the first store
    if (!currentShopId && stores.length > 0) {
      currentShopId = stores[0].id
    }

    // Set localStorage and fetch shop details
    if (currentShopId) {
      // Centralized localStorage writes - this is the single source of truth
      localStorage.setItem('selected_store', currentShopId)
      localStorage.setItem('shop_id', currentShopId)

      // Fetch shop details for the selected store if we have a user token
      if (authService.isUserToken()) {
        try {
          await fetchShopDetailsForStore(currentShopId)
        } catch (err) {
          // Failed to fetch shop details for selected store
        }
      }
    }
    syncSelectedStoreLocalIdFromStorage()
  }

  const fetchShopDetailsForStore = async (_storeId: string): Promise<void> => {
    // x-shop-id header is set via selected_store in local storage
    const { useShopStore } = await import('./shop-store')
    const shopStore = useShopStore()
    await shopStore.fetchShopDetails({ force: true })
  }

  const syncShopWithUserAfterCallback = async (): Promise<{
    linked: boolean
    linkedToAnotherUser?: boolean
    linkNeeded?: boolean
  } | null> => {
    if (!isAuthenticated.value || !authService.isUserToken()) {
      return null
    }

    try {
      const { apiService } = await import('../services/api')
      const shopToken = authService.getShopToken()

      if (!shopToken) {
        return { linked: false, linkNeeded: false }
      }

      // Get shop details with shop token
      const shopData = await apiService.getShopDetailsWithToken(shopToken)
      const ownedAccount = shopData?.owned_account
      const currentUserId = user.value?.id
      const currentUserEmail = user.value?.email

      // If shop is already linked to current user (id or email match)
      if (ownedAccount && (ownedAccount === currentUserId || ownedAccount === currentUserEmail)) {
        // Shop is linked to current user
        await updateSelectedStore(userStores.value)
        return { linked: true }
      }

      // If shop is linked to another user
      if (ownedAccount && ownedAccount !== currentUserId && ownedAccount !== currentUserEmail) {
        return { linked: false, linkedToAnotherUser: true }
      }

      // Shop is not linked - try to link it
      if (!ownedAccount) {
        try {
          const shopToken = authService.getShopToken()
          if (shopToken) {
            await apiService.linkStore({ shop_jwt: shopToken })
          } else {
            return { linked: false, linkNeeded: true }
          }
          // Refresh user stores after linking
          await fetchUserStores()
          await updateSelectedStore(userStores.value)
          return { linked: true }
        } catch (err) {
          return { linked: false, linkNeeded: true }
        }
      }

      return { linked: false, linkNeeded: false }
    } catch (error) {
      return null
    }
  }

  const setNeedsStoreLink = (value: boolean) => {
    needsStoreLink.value = value
  }

  const setShopLinkedToAnotherUser = (value: boolean) => {
    shopLinkedToAnotherUser.value = value
  }

  return {
    // State
    user,
    token,
    shopDetails, // Computed property that accesses shop-store
    isLoading,
    error,
    needsStoreLink,
    shopLinkedToAnotherUser,
    userStores,
    storesLoaded,
    selectedStoreLocalId,

    // Computed
    isAuthenticated,
    isUserToken,
    isShopToken,
    tokenType,
    bearerToken,
    isInstallCompleted, // Computed property that accesses shop-store

    // Actions
    initializeAuth,
    handleShopifyCallback,
    login,
    logout,
    redirectToShopifyAuth,
    clearError,
    verifyShopAccess,
    updateJWT,
    userLogin,
    userSignup,
    fetchCurrentUser,
    fetchUserStores,
    clearStoresCache,
    syncShopWithUserAfterCallback,
    setNeedsStoreLink,
    setShopLinkedToAnotherUser,
    fetchShopDetailsForStore,
    updateSelectedStore,
    handleShopJwt,
    syncSelectedStoreLocalIdFromStorage,
  }
})
